import React, { useState } from 'react';
import { ConsultarLeilaoPrimeResult, CLASSIFICACAO_INFO, PARECER_INFO } from '../types';
import { 
  ArrowLeft, 
  Gavel,
  Car,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Calendar,
  FileText,
  Image,
  Cpu,
  ChevronDown,
  ChevronUp,
  Building,
  Hash,
  Wrench,
  AlertOctagon,
  ShieldCheck,
  ShieldAlert,
  Eye,
  ExternalLink,
  Info
} from 'lucide-react';

interface ConsultaLeilaoPrimeResultProps {
  data: ConsultarLeilaoPrimeResult;
  onBack: () => void;
}

const ConsultaLeilaoPrimeResult: React.FC<ConsultaLeilaoPrimeResultProps> = ({ data, onBack }) => {
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({
    leiloes: true,
    parecer: true,
    remarketing: false,
    ia: false,
    classificacoes: false,
  });

  const toggleSection = (section: string) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  const classInfo = CLASSIFICACAO_INFO[data.classificacao] || CLASSIFICACAO_INFO[''];
  const parecerInfo = PARECER_INFO[data.parecerTecnico.parecer] || PARECER_INFO.favoravel;

  const getStatusIcon = (status: string) => {
    if (status === 'sim') return <XCircle className="w-4 h-4 text-red-400" />;
    if (status === 'nao') return <CheckCircle className="w-4 h-4 text-green-400" />;
    return <AlertTriangle className="w-4 h-4 text-amber-400" />;
  };

  const getStatusColor = (status: string) => {
    if (status === 'sim') return 'text-red-400';
    if (status === 'nao') return 'text-green-400';
    return 'text-amber-400';
  };

  const getSeverityColor = (severidade: string) => {
    if (severidade === 'alta') return 'bg-red-500/20 border-red-500/30 text-red-400';
    if (severidade === 'media') return 'bg-amber-500/20 border-amber-500/30 text-amber-400';
    return 'bg-green-500/20 border-green-500/30 text-green-400';
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-rose-950/10 to-slate-950">
      {/* Background pattern */}
      <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiMyMDI5M2EiIGZpbGwtb3BhY2l0eT0iMC40Ij48cGF0aCBkPSJNMzYgMzRoLTJ2LTRoMnY0em0wLTZ2LTRoLTJ2NGgyek0yNiAyNGgtMnY0aDJ2LTR6bTAgNmgtMnY0aDJ2LTR6Ii8+PC9nPjwvZz48L3N2Zz4=')] opacity-30" />
      
      <div className="relative max-w-4xl mx-auto px-4 py-8 space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <button 
            onClick={onBack}
            className="flex items-center gap-2 text-slate-400 hover:text-white transition-colors group"
          >
            <ArrowLeft className="w-5 h-5 group-hover:-translate-x-1 transition-transform" />
            <span>Voltar</span>
          </button>
        </div>

        {/* Main Card */}
        <div className="bg-gradient-to-br from-slate-900/95 to-slate-800/95 rounded-3xl border border-slate-700/50 overflow-hidden backdrop-blur-sm shadow-2xl">
          {/* Header Banner */}
          <div className="bg-gradient-to-r from-rose-600 to-red-600 p-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-white/20 rounded-2xl backdrop-blur-sm">
                <Gavel className="w-10 h-10 text-white" />
              </div>
              <div className="flex-1">
                <h1 className="text-2xl font-bold text-white">Registro de Leilão Prime</h1>
                <p className="text-rose-100 text-sm mt-1">
                  Histórico completo com análise de danos por IA
                </p>
              </div>
            </div>
          </div>

          {/* Vehicle Info */}
          <div className="p-6 border-b border-slate-700/50">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-slate-800/50 rounded-xl">
                <Car className="w-6 h-6 text-slate-400" />
              </div>
              <div className="flex-1">
                <p className="text-2xl font-mono font-bold text-white tracking-wider">{data.placa}</p>
                <p className="text-sm text-slate-400">Placa consultada</p>
              </div>
              <div className="text-right">
                <p className="text-xs text-slate-500">Consultado em</p>
                <p className="text-sm text-slate-400">{data.dataConsulta}</p>
              </div>
            </div>
          </div>

          {/* Summary Cards */}
          <div className="p-6 grid grid-cols-1 md:grid-cols-3 gap-4 border-b border-slate-700/50">
            {/* Classificação */}
            <div className={`p-5 rounded-2xl border ${classInfo.bgColor} ${classInfo.borderColor}`}>
              <p className="text-sm text-slate-400 mb-2">Classificação</p>
              <div className="flex items-center gap-3">
                <span className={`text-4xl font-bold ${classInfo.color}`}>
                  {data.classificacao || '-'}
                </span>
                <span className={`text-sm ${classInfo.color}`}>
                  {classInfo.label}
                </span>
              </div>
            </div>

            {/* Parecer Técnico */}
            <div className={`p-5 rounded-2xl border ${parecerInfo.bgColor} ${parecerInfo.borderColor}`}>
              <p className="text-sm text-slate-400 mb-2">Parecer Técnico</p>
              <div className="flex items-center gap-2">
                {data.parecerTecnico.parecer === 'favoravel' && <ShieldCheck className={`w-6 h-6 ${parecerInfo.color}`} />}
                {data.parecerTecnico.parecer === 'desfavoravel' && <ShieldAlert className={`w-6 h-6 ${parecerInfo.color}`} />}
                {data.parecerTecnico.parecer === 'alerta' && <AlertTriangle className={`w-6 h-6 ${parecerInfo.color}`} />}
                <span className={`text-xl font-bold ${parecerInfo.color}`}>
                  {data.parecerTecnico.parecerLabel}
                </span>
              </div>
            </div>

            {/* Status Leilão */}
            <div className={`p-5 rounded-2xl border ${data.possuiRegistroLeilao ? 'bg-red-500/10 border-red-500/30' : 'bg-green-500/10 border-green-500/30'}`}>
              <p className="text-sm text-slate-400 mb-2">Registro de Leilão</p>
              <div className="flex items-center gap-2">
                {data.possuiRegistroLeilao ? (
                  <>
                    <AlertOctagon className="w-6 h-6 text-red-400" />
                    <span className="text-xl font-bold text-red-400">Encontrado</span>
                  </>
                ) : (
                  <>
                    <CheckCircle className="w-6 h-6 text-green-400" />
                    <span className="text-xl font-bold text-green-400">Não Encontrado</span>
                  </>
                )}
              </div>
            </div>
          </div>

          {/* Content Sections */}
          <div className="p-6 space-y-4">
            {/* Classificação Info */}
            {data.classificacao && data.classificacaoInfo && (
              <div className={`p-5 rounded-2xl border ${classInfo.bgColor} ${classInfo.borderColor}`}>
                <div className="flex items-start gap-3">
                  <Info className={`w-5 h-5 mt-0.5 ${classInfo.color}`} />
                  <div>
                    <p className={`font-semibold ${classInfo.color}`}>
                      Classificação {data.classificacao}: {data.classificacaoInfo.titulo}
                    </p>
                    <p className="text-sm text-slate-300 mt-2">
                      {data.classificacaoInfo.descricao}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Registros de Leilão */}
            {data.registrosLeilao.length > 0 && (
              <div className="border border-slate-700/50 rounded-2xl overflow-hidden">
                <button
                  onClick={() => toggleSection('leiloes')}
                  className="w-full flex items-center justify-between p-4 bg-slate-800/30 hover:bg-slate-800/50 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <Gavel className="w-5 h-5 text-rose-400" />
                    <span className="font-semibold text-white">
                      Histórico de Leilões ({data.registrosLeilao.length})
                    </span>
                  </div>
                  {expandedSections.leiloes ? (
                    <ChevronUp className="w-5 h-5 text-slate-400" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-slate-400" />
                  )}
                </button>
                
                {expandedSections.leiloes && (
                  <div className="p-4 space-y-3">
                    {data.registrosLeilao.map((leilao, index) => (
                      <div key={index} className="p-4 bg-slate-800/30 rounded-xl border border-slate-700/30">
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                          <div>
                            <p className="text-slate-500">Data</p>
                            <p className="text-white font-medium">{leilao.dataLeilao}</p>
                          </div>
                          <div>
                            <p className="text-slate-500">Lote</p>
                            <p className="text-white font-medium">{leilao.lote}</p>
                          </div>
                          {leilao.comitente && (
                            <div className="col-span-2">
                              <p className="text-slate-500">Comitente</p>
                              <p className="text-white font-medium">{leilao.comitente}</p>
                            </div>
                          )}
                          {leilao.segmento && (
                            <div>
                              <p className="text-slate-500">Segmento</p>
                              <p className="text-white font-medium">{leilao.segmento}</p>
                            </div>
                          )}
                          {leilao.anoFabricacao && (
                            <div>
                              <p className="text-slate-500">Ano Fab.</p>
                              <p className="text-white font-medium">{leilao.anoFabricacao}</p>
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Parecer Técnico Detalhado */}
            <div className="border border-slate-700/50 rounded-2xl overflow-hidden">
              <button
                onClick={() => toggleSection('parecer')}
                className="w-full flex items-center justify-between p-4 bg-slate-800/30 hover:bg-slate-800/50 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <FileText className="w-5 h-5 text-blue-400" />
                  <span className="font-semibold text-white">Parecer Técnico</span>
                </div>
                {expandedSections.parecer ? (
                  <ChevronUp className="w-5 h-5 text-slate-400" />
                ) : (
                  <ChevronDown className="w-5 h-5 text-slate-400" />
                )}
              </button>
              
              {expandedSections.parecer && (
                <div className="p-4">
                  <div className="grid grid-cols-2 gap-3">
                    <div className="flex items-center justify-between p-3 bg-slate-800/30 rounded-xl">
                      <span className="text-sm text-slate-400">Vistorias Negadas</span>
                      <div className="flex items-center gap-2">
                        {getStatusIcon(data.parecerTecnico.detalhes.vistoriasNegadas.status)}
                        <span className={`text-sm font-medium ${getStatusColor(data.parecerTecnico.detalhes.vistoriasNegadas.status)}`}>
                          {data.parecerTecnico.detalhes.vistoriasNegadas.label}
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center justify-between p-3 bg-slate-800/30 rounded-xl">
                      <span className="text-sm text-slate-400">Frota Locadora</span>
                      <div className="flex items-center gap-2">
                        {getStatusIcon(data.parecerTecnico.detalhes.frotaLocadora.status)}
                        <span className={`text-sm font-medium ${getStatusColor(data.parecerTecnico.detalhes.frotaLocadora.status)}`}>
                          {data.parecerTecnico.detalhes.frotaLocadora.label}
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center justify-between p-3 bg-slate-800/30 rounded-xl">
                      <span className="text-sm text-slate-400">Indícios de Acidentes</span>
                      <div className="flex items-center gap-2">
                        {getStatusIcon(data.parecerTecnico.detalhes.indiciosAcidentes.status)}
                        <span className={`text-sm font-medium ${getStatusColor(data.parecerTecnico.detalhes.indiciosAcidentes.status)}`}>
                          {data.parecerTecnico.detalhes.indiciosAcidentes.label}
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center justify-between p-3 bg-slate-800/30 rounded-xl">
                      <span className="text-sm text-slate-400">Veículo Importado</span>
                      <div className="flex items-center gap-2">
                        {getStatusIcon(data.parecerTecnico.detalhes.veiculoImportado.status)}
                        <span className={`text-sm font-medium ${getStatusColor(data.parecerTecnico.detalhes.veiculoImportado.status)}`}>
                          {data.parecerTecnico.detalhes.veiculoImportado.label}
                        </span>
                      </div>
                    </div>
                  </div>
                  
                  {/* Sinistro Status */}
                  <div className={`mt-4 p-4 rounded-xl border ${data.possuiSinistro ? 'bg-red-500/10 border-red-500/30' : 'bg-green-500/10 border-green-500/30'}`}>
                    <div className="flex items-center gap-3">
                      {data.possuiSinistro ? (
                        <>
                          <AlertOctagon className="w-5 h-5 text-red-400" />
                          <span className="text-red-400 font-medium">Registro de Sinistro/Acidente Encontrado</span>
                        </>
                      ) : (
                        <>
                          <CheckCircle className="w-5 h-5 text-green-400" />
                          <span className="text-green-400 font-medium">Sem Registro de Sinistro/Acidente</span>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Remarketing */}
            {data.possuiRemarketing && data.registrosRemarketing.length > 0 && (
              <div className="border border-slate-700/50 rounded-2xl overflow-hidden">
                <button
                  onClick={() => toggleSection('remarketing')}
                  className="w-full flex items-center justify-between p-4 bg-slate-800/30 hover:bg-slate-800/50 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <Building className="w-5 h-5 text-purple-400" />
                    <span className="font-semibold text-white">
                      Remarketing ({data.registrosRemarketing.length})
                    </span>
                  </div>
                  {expandedSections.remarketing ? (
                    <ChevronUp className="w-5 h-5 text-slate-400" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-slate-400" />
                  )}
                </button>
                
                {expandedSections.remarketing && (
                  <div className="p-4 space-y-3">
                    {data.registrosRemarketing.map((item, index) => (
                      <div key={index} className="p-4 bg-slate-800/30 rounded-xl border border-slate-700/30">
                        <div className="grid grid-cols-2 gap-4 text-sm">
                          <div>
                            <p className="text-slate-500">Organizador</p>
                            <p className="text-white font-medium">{item.organizador}</p>
                          </div>
                          <div>
                            <p className="text-slate-500">Data Evento</p>
                            <p className="text-white font-medium">{item.dataEvento}</p>
                          </div>
                          {item.condicaoGeralVeiculo && (
                            <div>
                              <p className="text-slate-500">Condição Geral</p>
                              <p className="text-white font-medium">{item.condicaoGeralVeiculo}</p>
                            </div>
                          )}
                          {item.condicaoMotor && (
                            <div>
                              <p className="text-slate-500">Motor</p>
                              <p className="text-white font-medium">{item.condicaoMotor}</p>
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                    
                    {/* Fotos Remarketing */}
                    {data.fotosRemarketing.length > 0 && (
                      <div className="mt-4">
                        <p className="text-sm text-slate-400 mb-2">Fotos do Veículo</p>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                          {data.fotosRemarketing.map((foto, index) => (
                            <a
                              key={index}
                              href={foto}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="aspect-video bg-slate-800 rounded-lg flex items-center justify-center hover:bg-slate-700 transition-colors group"
                            >
                              <Image className="w-8 h-8 text-slate-500 group-hover:text-slate-300" />
                            </a>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Análise por IA */}
            {data.analiseIA.situacao === 'concluido' && (data.analiseIA.danos.length > 0 || data.analiseIA.pecasDanificadas.length > 0) && (
              <div className="border border-slate-700/50 rounded-2xl overflow-hidden">
                <button
                  onClick={() => toggleSection('ia')}
                  className="w-full flex items-center justify-between p-4 bg-slate-800/30 hover:bg-slate-800/50 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <Cpu className="w-5 h-5 text-cyan-400" />
                    <span className="font-semibold text-white">Análise de Danos por IA</span>
                    <span className="px-2 py-0.5 bg-cyan-500/20 text-cyan-400 text-xs rounded-full">
                      {data.analiseIA.danos.length} dano(s) detectado(s)
                    </span>
                  </div>
                  {expandedSections.ia ? (
                    <ChevronUp className="w-5 h-5 text-slate-400" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-slate-400" />
                  )}
                </button>
                
                {expandedSections.ia && (
                  <div className="p-4 space-y-4">
                    {/* Danos Detectados */}
                    {data.analiseIA.danos.length > 0 && (
                      <div>
                        <p className="text-sm font-medium text-slate-400 mb-3">Possíveis Danos Detectados</p>
                        <div className="space-y-2">
                          {data.analiseIA.danos.map((dano, index) => (
                            <div key={index} className={`p-4 rounded-xl border ${getSeverityColor(dano.severidade)}`}>
                              <div className="flex items-start justify-between">
                                <div>
                                  <p className="font-medium text-white">{dano.local}</p>
                                  <p className="text-sm text-slate-300 mt-1">{dano.descricao}</p>
                                </div>
                                <div className="text-right">
                                  <p className="text-2xl font-bold">{dano.probabilidade}%</p>
                                  <p className="text-xs text-slate-500">probabilidade</p>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    {/* Peças Danificadas */}
                    {data.analiseIA.pecasDanificadas.length > 0 && (
                      <div>
                        <p className="text-sm font-medium text-slate-400 mb-3">
                          <Wrench className="w-4 h-4 inline mr-2" />
                          Peças Possivelmente Danificadas
                        </p>
                        <div className="grid grid-cols-2 gap-2">
                          {data.analiseIA.pecasDanificadas.map((peca, index) => (
                            <div key={index} className="flex items-center justify-between p-3 bg-slate-800/30 rounded-lg">
                              <span className="text-sm text-slate-300">{peca.descricao}</span>
                              <span className={`text-sm font-bold ${peca.probabilidade >= 80 ? 'text-red-400' : peca.probabilidade >= 50 ? 'text-amber-400' : 'text-slate-400'}`}>
                                {peca.probabilidade}%
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    {/* Imagens IA */}
                    {data.analiseIA.imagens.length > 0 && (
                      <div>
                        <p className="text-sm font-medium text-slate-400 mb-3">Imagens da Análise</p>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                          {data.analiseIA.imagens.map((img, index) => (
                            <a
                              key={index}
                              href={img}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="aspect-video bg-slate-800 rounded-lg overflow-hidden group relative"
                            >
                              <img 
                                src={img} 
                                alt={`Análise ${index + 1}`}
                                className="w-full h-full object-cover"
                                onError={(e) => {
                                  (e.target as HTMLImageElement).style.display = 'none';
                                  (e.target as HTMLImageElement).parentElement!.innerHTML = '<div class="flex items-center justify-center h-full"><span class="text-slate-500 text-xs">Imagem</span></div>';
                                }}
                              />
                              <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                                <ExternalLink className="w-6 h-6 text-white" />
                              </div>
                            </a>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Dicionário de Classificações */}
            <div className="border border-slate-700/50 rounded-2xl overflow-hidden">
              <button
                onClick={() => toggleSection('classificacoes')}
                className="w-full flex items-center justify-between p-4 bg-slate-800/30 hover:bg-slate-800/50 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <Info className="w-5 h-5 text-slate-400" />
                  <span className="font-semibold text-white">Legenda das Classificações</span>
                </div>
                {expandedSections.classificacoes ? (
                  <ChevronUp className="w-5 h-5 text-slate-400" />
                ) : (
                  <ChevronDown className="w-5 h-5 text-slate-400" />
                )}
              </button>
              
              {expandedSections.classificacoes && (
                <div className="p-4 space-y-3">
                  {Object.entries(data.dicionarioClassificacoes).map(([key, info]) => {
                    const cls = CLASSIFICACAO_INFO[key] || CLASSIFICACAO_INFO[''];
                    return (
                      <div key={key} className={`p-4 rounded-xl border ${cls.bgColor} ${cls.borderColor}`}>
                        <div className="flex items-center gap-3 mb-2">
                          <span className={`text-2xl font-bold ${cls.color}`}>{key}</span>
                          <span className={`font-medium ${cls.color}`}>{info.titulo}</span>
                        </div>
                        <p className="text-sm text-slate-300">{info.descricao}</p>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="text-center text-xs text-slate-500 pb-4">
          Consulta realizada via Pedigree do Carro • {data.apiSource}
        </div>
      </div>
    </div>
  );
};

export default ConsultaLeilaoPrimeResult;

